// Form pieces of the signed-out screens. Native inputs with explicit labels, errors announced to
// assistive tech, and the autocomplete hints password managers rely on.
import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PasswordPolicy } from '../../auth/authMode';
import { passwordRules } from '../../auth/passwordPolicy';

const ERROR_TEXT = '#ff8f8f'; // --ui-error (#ff0000) is too harsh as body text on the dark card
const FOCUS_RING = '0 0 0 3px rgba(247, 148, 32, 0.25)';

const inputSx = (invalid: boolean) => ({
  width: '100%',
  height: 44,
  boxSizing: 'border-box' as const,
  px: '12px',
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  color: 'var(--text-text-primary, #fdfdfd)',
  background: 'var(--stroke-solid-black, #0f0f0f)',
  border: `1px solid ${invalid ? 'var(--ui-error, #ff0000)' : 'var(--stroke-form-input-border-dim, #2a2a2a)'}`,
  borderRadius: '8px',
  outline: 'none',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  '&::placeholder': { color: 'var(--text-text-tertiary, #868686)' },
  '&:hover': { borderColor: invalid ? 'var(--ui-error, #ff0000)' : 'var(--stroke-divider, #444444)' },
  '&:focus-visible': { borderColor: 'var(--stroke-highlight, #f79420)', boxShadow: FOCUS_RING },
  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
  // Chrome paints autofilled fields light; keep them on the dark surface.
  '&:-webkit-autofill': {
    WebkitTextFillColor: 'var(--text-text-primary, #fdfdfd)',
    WebkitBoxShadow: '0 0 0 1000px #0f0f0f inset',
    caretColor: '#fdfdfd'
  },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
});

interface FieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  id: string;
  label: string;
  error?: string;
  hint?: React.ReactNode;
  /** rendered inside the field, on the right (the show/hide control) */
  adornment?: React.ReactNode;
}

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  ({ id, label, error, hint, adornment, 'aria-describedby': describedByProp, ...input }, ref) => {
    const describedBy =
      [error ? `${id}-error` : '', hint ? `${id}-hint` : '', describedByProp || ''].filter(Boolean).join(' ') ||
      undefined;
    return (
      <Box sx={{ mb: '16px' }}>
        <Box
          component="label"
          htmlFor={id}
          sx={{
            display: 'block',
            mb: '6px',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: '18px',
            color: 'var(--text-text-secondary, #b6b6b6)'
          }}
        >
          {label}
        </Box>
        <Box sx={{ position: 'relative' }}>
          <Box
            component="input"
            ref={ref}
            id={id}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            sx={{ ...inputSx(Boolean(error)), ...(adornment ? { pr: '64px' } : {}) }}
            {...input}
          />
          {adornment ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                pr: '6px'
              }}
            >
              {adornment}
            </Box>
          ) : null}
        </Box>
        {error ? (
          <Box id={`${id}-error`} role="alert" sx={{ mt: '6px', fontSize: 13, lineHeight: '18px', color: ERROR_TEXT }}>
            {error}
          </Box>
        ) : null}
        {hint ? (
          <Box
            id={`${id}-hint`}
            sx={{ mt: '6px', fontSize: 12, lineHeight: '18px', color: 'var(--text-text-tertiary, #868686)' }}
          >
            {hint}
          </Box>
        ) : null}
      </Box>
    );
  }
);
Field.displayName = 'Field';

type PasswordFieldProps = Omit<FieldProps, 'type' | 'adornment'>;

/** Password input with a show/hide control (a real button: reachable by keyboard, state announced). */
export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>((props, ref) => {
  const [visible, setVisible] = useState(false);
  return (
    <Field
      ref={ref}
      type={visible ? 'text' : 'password'}
      spellCheck={false}
      autoCapitalize="none"
      adornment={
        <Box
          component="button"
          type="button"
          aria-pressed={visible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible((v) => !v)}
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            cursor: 'pointer',
            px: '10px',
            py: '6px',
            borderRadius: '6px',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-text-secondary, #b6b6b6)',
            '&:hover': { color: 'var(--text-text-primary, #fdfdfd)' },
            '&:focus-visible': { boxShadow: FOCUS_RING }
          }}
        >
          {visible ? 'Hide' : 'Show'}
        </Box>
      }
      {...props}
    />
  );
});
PasswordField.displayName = 'PasswordField';

/** The password rules as a live checklist: each line turns green when the input satisfies it. */
export const PasswordChecklist: React.FC<{ password: string; policy: PasswordPolicy; id?: string }> = ({
  password,
  policy,
  id
}) => (
  <Box component="ul" id={id} aria-label="Password requirements" sx={{ listStyle: 'none', m: '-6px 0 16px', p: 0 }}>
    {passwordRules(password, policy).map((rule) => (
      <Box
        component="li"
        key={rule.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: 12,
          lineHeight: '20px',
          color: rule.met ? '#5fd38a' : 'var(--text-text-tertiary, #868686)'
        }}
      >
        <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: 'currentColor' }} />
        {rule.label}
        <Box
          component="span"
          sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        >
          {rule.met ? ' — met' : ' — not met yet'}
        </Box>
      </Box>
    ))}
  </Box>
);

type NoticeTone = 'error' | 'info' | 'success';
const NOTICE: Record<NoticeTone, { border: string; background: string; color: string }> = {
  error: { border: 'rgba(255, 0, 0, 0.45)', background: 'rgba(255, 0, 0, 0.08)', color: ERROR_TEXT },
  info: {
    border: 'var(--stroke-border-border-dim, #2a2a2a)',
    background: 'rgba(255, 255, 255, 0.04)',
    color: 'var(--text-text-secondary, #b6b6b6)'
  },
  success: { border: 'rgba(0, 151, 45, 0.55)', background: 'rgba(0, 151, 45, 0.10)', color: '#7fe3a3' }
};

/**
 * A message about the whole form. Errors interrupt (role=alert); the rest is announced politely.
 * `focus`: the message replaces what the person was working in (a form that is gone now) — the
 * focus moves to it, so keyboard and screen-reader users are not left on nothing.
 */
export const Notice: React.FC<{ tone: NoticeTone; children: React.ReactNode; focus?: boolean }> = ({
  tone,
  children,
  focus
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (focus) ref.current?.focus();
  }, [focus]);
  return (
    <Box
      ref={ref}
      tabIndex={focus ? -1 : undefined}
      role={tone === 'error' ? 'alert' : 'status'}
      sx={{
        mb: '16px',
        p: '10px 12px',
        borderRadius: '8px',
        border: `1px solid ${NOTICE[tone].border}`,
        background: NOTICE[tone].background,
        color: NOTICE[tone].color,
        fontSize: 13,
        lineHeight: '20px',
        '&:focus-visible': { outline: 'none', boxShadow: FOCUS_RING }
      }}
    >
      {children}
    </Box>
  );
};

/** In-app link in the brand's orange (router link: the hash route changes, nothing reloads). */
export const TextLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Box
    component={RouterLink}
    to={to}
    sx={{
      color: 'var(--stroke-highlight, #f79420)',
      fontWeight: 600,
      textDecoration: 'none',
      borderRadius: '4px',
      '&:hover': { textDecoration: 'underline' },
      '&:focus-visible': { outline: 'none', boxShadow: FOCUS_RING }
    }}
  >
    {children}
  </Box>
);
